import type {
  PlayerResponse,
  Schemas,
} from "@softmax/observatory-client/types";
import {
  apiDelete,
  apiPost,
  apiPostForm,
  type ApiRequestOptions,
} from "@softmax/observatory-client/client";
import { maxAvatarUploadBytes } from "@/lib/upload-limits";

export function createPlayer({
  name,
  options = { elevatedPrivileges: false },
}: {
  name: string;
  options?: ApiRequestOptions | undefined;
}): Promise<PlayerResponse> {
  return apiPost<PlayerResponse>("/players", { name }, options);
}

export async function uploadPlayerAvatar({
  playerId,
  image,
  options = { elevatedPrivileges: false },
}: {
  playerId: string;
  image: File;
  options?: ApiRequestOptions | undefined;
}): Promise<string | null> {
  if (image.size > maxAvatarUploadBytes)
    throw new Error("Image must be 5 MB or smaller");

  const form = new FormData();
  form.append("file", image);
  const response = await apiPostForm<Schemas["AvatarResponse"]>(
    `/v2/players/${encodeURIComponent(playerId)}/avatar`,
    form,
    options,
  );
  return response.avatar_url ?? null;
}

export async function deletePlayerAvatar({
  playerId,
  options = { elevatedPrivileges: false },
}: {
  playerId: string;
  options?: ApiRequestOptions | undefined;
}): Promise<void> {
  await apiDelete<Schemas["AvatarResponse"]>(
    `/v2/players/${encodeURIComponent(playerId)}/avatar`,
    undefined,
    options,
  );
}

export function setDefaultPlayer({
  playerId,
  options = { elevatedPrivileges: false },
}: {
  playerId: string;
  options?: ApiRequestOptions | undefined;
}): Promise<PlayerResponse> {
  return apiPost<PlayerResponse>(
    `/players/${encodeURIComponent(playerId)}/default`,
    {},
    options,
  );
}

export type SavedPlayer = {
  id: string;
  name: string;
  avatarUrl: string | null;
};
